<template>
	<v-dialog :model-value="dialog.open" width="600" persistent>
		<v-card>
			<v-card-title class="text-h1 font-weight-bold">
				Update event
				<v-spacer></v-spacer>
				<v-btn icon="mdi-close" @click="close" :disabled="loading" variant="plain">
				</v-btn>
			</v-card-title>
			<v-card-text>
				<v-form ref="form" v-model="valid">
					<v-row>
						<v-col>
							<label class="text-caption">Title</label>
							<v-text-field :model-value="event.name" variant="outlined" density="compact"
								hide-details disabled/>
						</v-col>
					</v-row>
					<v-row class="pl-1">
						<div class="d-flex align-center">
							<v-checkbox v-model="event.isOverride" label="Override" density="compact" hide-details
								:disabled="event.schedulerSubscription?.type !== 'Authorised' || event.nowPlaying">
							</v-checkbox>
							<div v-if="event.schedulerSubscription?.type !== 'Authorised'" class="d-inline-flex pl-2">
								<span class="text-caption font-weight-light" v-if="!event.nowPlaying">
								(This option is available only under Show Authorization relationship.)
								</span>
							</div>
						</div>
					</v-row>
					<v-row>
						<v-col>
							<label class="text-caption">Priority</label>
							<v-select v-model="event.priority"
								:items="priorityLanes"
								item-title="name"
								item-value="priority"
								:disabled="!event.isOverride || event.nowPlaying"
								variant="outlined" density="compact"
								single-line hide-details/>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="4">
							<label class="text-caption">Start date</label>
							<v-menu v-model="datepickerOpen" :close-on-content-click="false">
								<template v-slot:activator="{ props }">
									<v-text-field
										v-bind="props"
										:model-value="startDate.format('YYYY-MM-DD')"
										:disabled="!event.isOverride || event.nowPlaying"
										:error="!dateValid"
										variant="outlined" density="compact"
										flat single-line hide-details
										placeholder="Select date"
										append-inner-icon="mdi-calendar"></v-text-field>
								</template>
								<v-date-picker
									:model-value="startDate"
									:min="today"
									color="secondary"
									hide-header
									show-adjacent-months
									@update:model-value="onUpdateStartDate">
								</v-date-picker>
							</v-menu>
						</v-col>
						<v-col cols="2">
							<label class="text-caption">Hour</label>
							<v-text-field :model-value="startDate.format('HH')"
								type="number" min="0" max="23" placeholder="00"
								variant="outlined" density="compact"
								:error="!dateValid"
								:disabled="!event.isOverride || event.nowPlaying"
								single-line hide-details
								@update:model-value="onUpdateStartTime('hour', $event)"/>
						</v-col>
						<v-col cols="2">
							<label class="text-caption">Minute</label>
							<v-text-field :model-value="startDate.format('mm')"
								type="number" min="0" max="59" placeholder="00"
								variant="outlined" density="compact"
								:error="!dateValid"
								:disabled="!event.isOverride || event.nowPlaying"
								single-line hide-details
								@update:model-value="onUpdateStartTime('minute', $event)"/>
						</v-col>
					</v-row>
					<v-row v-if="!dateValid">
						<label class="text-caption px-3" style="color: #d02e3d">
							Value must be same or after show start
						</label>
					</v-row>
					<v-row>
						<v-col cols="2">
							<label class="text-caption">Duration</label>
							<v-text-field :model-value="secondsToFormat(event.duration, 'HH:mm')" variant="outlined"
								density="compact" single-line hide-details
								:disabled="!event.isOverride || event.nowPlaying" readonly/>
						</v-col>
						<v-col cols="10">
							<v-range-slider
								v-model="event.range"
								:min="0"
								:max="event.duration_scheduled"
								:step="60" strict
								thumb-label color="secondary"
								:disabled="!event.isOverride || event.nowPlaying"
								class="align-center pt-6"
								hide-details
								@update:model-value="updateDuration">
								<template v-slot:prepend>
									<v-text-field
										:model-value="secondsToFormat(event.range[0], 'HH:mm:00')"
										:disabled="!event.isOverride || event.nowPlaying"
										density="compact"
										style="width: 70px"
										type="text" variant="outlined"
										hide-details single-line
										@update:model-value="updateSlider(0, $event)"
									></v-text-field>
								</template>
								<template v-slot:append>
									<v-text-field
										:model-value="secondsToFormat(event.range[1], 'HH:mm:00')"
										:disabled="!event.isOverride || event.nowPlaying"
										density="compact"
										style="width: 70px"
										type="text" variant="outlined"
										hide-details single-line
										@update:model-value="updateSlider(1, $event)"
									></v-text-field>
								</template>
								<template v-slot:thumb-label="item">
									{{ secondsToFormat(item.modelValue, 'HH:mm:00') }}
								</template>
							</v-range-slider>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="3" class="d-flex flex-column">
							<label class="text-caption">Status</label>
							<v-chip class="mt-1 justify-center" :color="statusColor">
								{{ event.status === 'COMPLETED' ? "SYNCED" : event.status }}
							</v-chip>
						</v-col>
						<v-col cols="3">
							<v-btn variant="outlined" color="secondary" @click="resetPlaylists(true)"
								:disabled="loading || event.nowPlaying" class="mt-4">
								{{ event.status === 'COMPLETED' ? "Reset" : "Push" }} Playlists
							</v-btn>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<!--For shows with integration type: feed-->
				<v-btn v-if="timeToExtend > 0" variant="outlined" color="secondary"
					:disabled="loading || event.nowPlaying"
					@click="openExtendDurationConfirmationDialog">
					Extend duration
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn variant="text" color="primary" @click="openCancelRestoreConfirmationDialog"
					:disabled="loading || event.nowPlaying">
					{{ !event.cancelled ? "Cancel" : "Restore" }} event
				</v-btn>
				<v-btn variant="outlined" color="primary" @click="close" :disabled="loading">
					Close
				</v-btn>
				<v-btn variant="flat" color="primary" @click="saveEvent"
					:disabled="!event.isOverride || !valid || loading || event.nowPlaying" :loading="loading">
					Save event
				</v-btn>
			</v-card-actions>
		</v-card>
		<v-dialog :model-value="confirmationDialog.open" width="560" persistent>
			<ExtendDurationConfirmation :type="confirmationDialog.type"
				:title="confirmationDialog.title"
				:message="confirmationDialog.message"
				:button-options="confirmationDialog.buttonOptions"
				:custom-variables="confirmationDialog.customVariables"
				:styles="confirmationDialog.styles"
				@agree="(d, c) => confirmationDialog.action(d, c)" @cancel="confirmationDialog.open=false"/>
		</v-dialog>
	</v-dialog>
</template>

<script>
/* Libraries */
import moment from "moment-timezone";
import emitter from "@/emitter";

/* Enums */
import ConfirmationType from "@/enums/ConfirmationType";
import PriorityLanes from "@/enums/PriorityLanes";
import ShowIntegrationType from "@/enums/ShowIntegrationType";

/* Mixins */
import DurationFormatMixin from "@/mixins/DurationFormatMixin";

/* Modules */
import ExtendDurationConfirmation from "@/components/dialogs/ExtendDurationConfirmation.vue";

export default {
	name: "EditExternalPlaylistEventDialog",

	components: {ExtendDurationConfirmation},

	mixins: [DurationFormatMixin],

	props: ["dialog", "options"],

	emits: ["delete", "close"],

	data() {
		return {
			event: {
				...this.options.event,
				isOverride: !!this.options.event.override_id,
				range: [this.options.event.offset, (this.options.event.offset + this.options.event.duration)]
			},
			show: this.options.show,
			parent: this.options.parent,
			valid: true,
			loading: false,
			priorityLanes: PriorityLanes.get().filter(lane => ![
				PriorityLanes.Scheduled.priority, PriorityLanes.Split.priority
			].includes(lane.priority)),
			today: moment().startOf("day"),
			datepickerOpen: false,
			dateValid: true,
			confirmationDialog: {
				open: false
			},
			timeToExtend: 0
		}
	},

	computed: {
		startDate() {
			return moment.tz(this.event.start, this.options.parent.stationTimezone);
		},

		startDateScheduled() {
			return moment.tz(this.event.start_scheduled, this.options.parent.stationTimezone);
		},

		statusColor() {
			return this.event.status === "COMPLETED" ? "green" : "default";
		}
	},

	methods: {
		onUpdateStartDate(selectedDate) {
			this.datepickerOpen = false;
			this.event.start = moment.tz(
				this.event.start,
				this.options.parent.stationTimezone
			).date(selectedDate.date()).valueOf();	// keep the time same
		},

		onUpdateStartTime(unit, value) {
			this.event.start = moment.tz(this.event.start, this.options.parent.stationTimezone).set(unit, value).valueOf();
		},

		updateDuration(range) {
			this.event.duration = range[1] - range[0];
		},

		updateSlider(index, val) {
			this.range[index] = moment.duration(val).asSeconds();
		},

		resetPlaylists(retrigger, callback) {
			this.loading = true;

			const params = {
				eventID: this.event.id,
				showID: this.event.object_id,
				stationID: this.parent.selectedStationID,
				overrideID: this.event.override_id,
				retrigger: retrigger
			};

			this.$store.dispatch("resetStationEvent", params)
				.then(response => {
					if (response.status === 202) {
						this.$store.commit(
							"SHOW_SUCCESS_NOTIFICATION",
							{
								message: `Playlists successfully ${this.event.status === 'COMPLETED' ? "reset" :
																   "pushed"}.`
							}
						);
					}
					else {
						this.$store.commit(
							"SHOW_NOTIFICATION_WITH_TYPE", {
								message: `An error occurred when trying to ${this.event.status === 'COMPLETED' ?
																			 "reset" : "push"} the playlists.`
							});
						this.loading = false;
					}
				})
				.finally(() => {
					if (callback) {
						callback();
					}
					else {
						this.loading = false;
					}
				});
		},

		saveEvent() {
			if (this.$refs.form.validate()) {
				this.loading = true;

				let payload = {
					event_id: this.event.id,
					schedule_id: this.event.schedule_id,
					subscriber_id: this.parent.selectedStationID,
					priority: this.event.priority,
					start: moment(this.event.start).utc().valueOf(),
					duration: this.event.duration,
					offset: this.event.range[0],
					color: this.event.color
				};

				if (this.event.override_id) {
					payload.id = this.event.override_id;
				}

				const createOverride = !payload.id;
				const action = createOverride ? "createEventOverride" : "updateEventOverride";

				const saveOverride = callback => {
					this.$store.dispatch(action, payload).then(response => {
							if (response.status === 200) {
								this.event.override_id = response.data.id;
								this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {message: "Event successfully updated."});

								if (createOverride) {
									this.resetPlaylists(true, callback);
								}
								else {
									callback();
								}
							}
							else {
								this.$store.commit(
									"SHOW_NOTIFICATION_WITH_TYPE",
									{message: `An error occurred when trying to update the event.`}
								);
								this.loading = false;
							}
						})
						.catch(error => {
							this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {message: error.response?.data?.message});
							this.loading = false;
						});
				};

				const onComplete = () => {
					this.loading = false;
					emitter.emit("playlistSelector:refreshCalendarAndReloadEvent", {
						id: this.event.id,
						override_id: this.event.override_id
					});
					this.close();
				};

				if (createOverride) {
					this.resetPlaylists(false, () => saveOverride(onComplete));
				}
				else {
					saveOverride(() => this.syncEvent(onComplete));
				}
			}
		},

		syncEvent(callback) {
			this.loading = true;

			const params = {
				showID: this.event.object_id,
				eventID: this.event.id,
				stationID: this.parent.selectedStationID,
				override_id: this.event.override_id
			};

			this.$store.dispatch("syncStationEvent", params)
				.then(response => {
					if (response.status !== 200) {
						this.$store.commit(
							"SHOW_NOTIFICATION_WITH_TYPE",
							{message: `An error occurred when trying to sync the event.`}
						)
					}
				})
				.finally(() => callback());
		},

		openCancelRestoreConfirmationDialog() {
			this.confirmationDialog = {
				open: true,
				type: ConfirmationType.General,
				title: `${!this.event.cancelled ? "Cancel" : "Restore"} event`,
				message: !this.event.cancelled ?
						 "Are you sure you want to cancel this event? Cancelling this event will cancel this episode for this station." :
						 "Are you sure you want to restore this event?",
				action: !this.event.cancelled ? this.cancelEvent : this.restoreEvent,
				buttonOptions: {
					agreeBtn: {
						enabled: true,
						message: "Confirm"
					},
					cancelBtn: {
						enabled: true,
						message: 'Cancel'
					}
				},
				styles: {
					messageClass: "text-left",
					agreeButton: {
						color: "primary",
						variant: "flat"
					},
					cancelButton: {
						color: "primary",
						variant: "text"
					}
				}
			}
		},

		cancelEvent() {
			this.loading = true;

			let data;

			if (!this.event.override_id) {
				data = {
					event_id: this.event.id,
					subscriber_id: this.parent.selectedStationID,
					schedule_id: this.event.schedule_id,
					priority: this.event.priority,
					start: this.event.start,
					duration: this.event.duration,
					offset: this.event.offset,
					color: this.event.color,
					cancelled: true
				};
			}
			else {
				data = {
					id: this.event.override_id,
					subscriber_id: this.parent.selectedStationID
				}
			}

			const createOverride = !this.event.override_id;
			const action = createOverride ? "createEventOverride" : "cancelEvent";

			const processCancellation = callback => {
				this.$store.dispatch(action, data).then(response => {
						if (response.status === 200) {
							this.event.override_id = response.data.id;
							this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {message: "Event successfully cancelled."});

							if (createOverride) {
								callback();
							}
							else {
								this.resetPlaylists(false, callback);
							}
						}
						else {
							this.$store.commit(
								"SHOW_NOTIFICATION_WITH_TYPE",
								{message: "An error occurred when trying to cancel the event."}
							);
							this.loading = false;
						}
					})
					.catch(error => {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {message: error.response.data.message});
						this.close();
						this.loading = false;
					});
			};

			const onComplete = () => {
				emitter.emit(
					"calendar:refreshCalendarAndReloadEvent",
					{id: this.event.id, override_id: this.event.override_id, cancelled: true}
				);
				this.parent.returnToCurrentPlaylist();
				this.close();
			};

			if (createOverride) {
				this.resetPlaylists(false, () => processCancellation(onComplete));
			}
			else {
				processCancellation(onComplete);
			}
		},

		restoreEvent() {
			this.loading = true;

			const params = {
				id: this.event.override_id,
				subscriber_id: this.event.subscriber_id
			}

			this.$store.dispatch("restoreEvent", params)
				.then(response => {
					if (response.status == 202) {
						this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {message: `Event successfully restored.`});

						emitter.emit("calendar:refreshCalendarAndReloadEvent", {
							id: this.event.id,
							override_id: this.event.override_id
						});
						this.close();
					}
					else {
						this.$store.commit(
							"SHOW_NOTIFICATION_WITH_TYPE",
							{message: `An error occurred when trying to restore the event.`}
						)
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},

		openExtendDurationConfirmationDialog() {
			this.confirmationDialog = {
				open: true,
				type: ConfirmationType.ExtendDuration,
				title: "Extend event above show's duration",
				action: this.extendDuration,
				customVariables: {
					timeToExtend: this.timeToExtend
				},
				buttonOptions: {
					agreeBtn: {
						enabled: true,
						message: "Extend"
					},
					cancelBtn: {
						enabled: true,
						message: "Cancel"
					}
				},
				styles: {
					messageClass: "text-left",
					agreeButton: {
						color: "primary",
						variant: "flat"
					},
					cancelButton: {
						color: "primary",
						variant: "text"
					}
				}
			}
		},

		extendDuration(durationToExtend, callback) {
			if (!durationToExtend || durationToExtend > this.timeToExtend) {
				return;
			}
			const payload = {
				id: this.event.id,
				schedule_id: this.event.schedule_id,
				object_id: this.event.object_id,
				slot_id: this.event.slot_id,
				priority: this.event.priority,
				start: this.event.start,
				mandatory: this.event.mandatory,
				duration: this.event.duration + (durationToExtend * 60)
			};

			this.$store.dispatch("updateScheduleEvent", payload)
				.then(response => {
					this.event.duration = response.data.duration;
					this.event.range[1] = this.event.offset + this.event.duration;

					this.syncShowEvent();

					// Skip the step of creating the Station Override IF the station/show relation is NOT Authorized
					// (which means skip for affiliates/hosts).
					if (this.event.schedulerSubscription?.type === "Authorised") {
						this.saveEvent();
					}
					else {
						this.syncEvent(() => {
							this.loading = false;
							emitter.emit("playlistSelector:refreshCalendarAndReloadEvent", this.event);
							this.close();
						});
					}
				})
				.catch(error => {
					this.$store.commit(
						"SHOW_NOTIFICATION_WITH_TYPE",
						{
							message: error.response.data?.message ??
									 `An error occurred when trying to extend the event's duration.`
						}
					);
				})
				.finally(() => callback());
		},

		syncShowEvent() {
			const params = {
				showID: this.event.object_id,
				eventID: this.event.id
			};

			this.$store.dispatch("syncShowEvent", params)
				.then(response => {
					if (response.status !== 200) {
						this.$store.commit(
							"SHOW_NOTIFICATION_WITH_TYPE",
							{message: `An error occurred when trying to sync the event.`}
						)
					}
				});
		},

		close() {
			this.$emit("close");
		}
	},

	watch: {
		startDate: {
			handler(value) {
				if (this.show.integration_type === ShowIntegrationType.Feed ||
					(this.show.integration_type === ShowIntegrationType.Originated &&
					 this.show.settings.host_name !== this.parent.selectedStationID)) {
					this.dateValid = value.isSameOrAfter(this.startDateScheduled);
				}
			},
			immediate: true	// force eager callback execution
		}
	},

	mounted() {
		// For future events of feed shows, fetch the settings to get the show's recording duration
		if (moment().isBefore(this.event.start_scheduled) &&
			this.show.integration_type === ShowIntegrationType.Feed &&
			this.show.settings?.recording_duration) {
			this.timeToExtend = Math.trunc(
				(this.show.settings.recording_duration - this.event.duration) / 60
			);
		}
	}
};
</script>